import React from 'react';
import {
  Scale,
  FileText,
  TriangleAlert,
  ShieldCheck,
  CreditCard,
  Ban,
  Gavel,
  Mail,
  Clock,
  Server,
} from 'lucide-react';

import {
  Anchor,
  Badge,
  Box,
  Container,
  Group,
  List,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
  useComputedColorScheme,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';

function SectionTitle({ icon, title }) {
  return (
    <Group gap="sm" mt="xl" mb="md">
      <ThemeIcon variant="light" color="blue" radius="xl">
        {icon}
      </ThemeIcon>
      <Title order={2}>{title}</Title>
    </Group>
  );
}

function SummaryItem({ icon, color, bold, text }) {
  return (
    <Group align="flex-start" wrap="nowrap" gap="sm">
      <ThemeIcon variant="light" color={color} radius="xl" size="md">
        {icon}
      </ThemeIcon>
      <Text fz="sm">
        <Text component="span" fw={700}>
          {bold}
        </Text>{' '}
        {text}
      </Text>
    </Group>
  );
}

function CodeInline({ children }) {
  return (
    <Text component="span" ff="monospace" fz="sm">
      {children}
    </Text>
  );
}

export default function TermsOfServicePage() {
  const { t } = useTranslation();
  const colorScheme = useComputedColorScheme('light');

  return (
    <Box mih="100vh" py={60}>
      <Container size="md">
        <Stack gap="lg">
          <Paper withBorder radius="xl" p="xl" bg={colorScheme === 'dark' ? 'dark.7' : 'gray.0'}>
            <Stack align="center" ta="center" gap="md">
              <Title order={1} fz="2.5rem" fw={800}>
                {t('Terms of Service')}
              </Title>
              <Badge variant="light" color="violet" size="lg" radius="xl" leftSection={<Clock size={14} />}>
                {t('Last Updated: March 18, 2026')}
              </Badge>
            </Stack>
          </Paper>

          <Paper withBorder radius="lg" p="xl">
            <Stack gap="lg">
              <Group gap="xs">
                <ThemeIcon variant="light" color="grape" radius="xl">
                  <Scale size={16} />
                </ThemeIcon>
                <Text fw={700} fz="lg">
                  {t('Terms at a Glance')}
                </Text>
              </Group>

              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
                <SummaryItem
                  icon={<ShieldCheck size={16} />}
                  color="teal"
                  bold={t('Your Code:')}
                  text={t('You own your code. We never read it; we only see the hashes.')}
                />
                <SummaryItem
                  icon={<TriangleAlert size={16} />}
                  color="orange"
                  bold={t('Immutability:')}
                  text={t('Blockchain anchors are permanent. They cannot be deleted or "undone".')}
                />
                <SummaryItem
                  icon={<Ban size={16} />}
                  color="red"
                  bold={t('No Abuse:')}
                  text={t("Don't use our service to timestamp illegal content or spam.")}
                />
                <SummaryItem
                  icon={<Gavel size={16} />}
                  color="blue"
                  bold={t('Legal Proof:')}
                  text={t('We provide mathematical evidence, not legal representation.')}
                />
              </SimpleGrid>
            </Stack>
          </Paper>

          <Text>
            {t('Welcome to Timestamp GIT. These Terms of Service ("Terms") govern your access to and use of our platform, APIs, GitHub App, and Docker containers. By using Timestamp GIT, you agree to be bound by these Terms.')}
          </Text>

          <SectionTitle icon={<FileText size={16} />} title={t('1. Acceptance of Terms')} />
          <Text>
            {t('By creating an account or using our services, you confirm that you can form a binding contract with Timestamp GIT and RSJ Software GmbH. If you are using the services on behalf of an organization, you agree to these Terms on behalf of that organization.')}
          </Text>

          <SectionTitle icon={<ShieldCheck size={16} />} title={t('2. Description of Service')} />
          <Text>
            {t('Timestamp GIT provides cryptographic proof-of-existence for digital files and Git commits. Our service calculates hashes, aggregates them into Merkle trees, and anchors the resulting Merkle Root into the Bitcoin blockchain via the OpenTimestamps protocol.')}
          </Text>
          <Paper withBorder p="md" radius="md" bg={colorScheme === 'dark' ? 'yellow.9' : 'yellow.0'}>
            <Text fz="sm">
              <Text component="span" fw={700}>
                {t('Note on Service Nature:')}{' '}
              </Text>
              {t('Timestamp GIT provides a technical service. The eventual "legal weight" of the evidence provided depends on the jurisdiction and the specific requirements of the court or authority. We provide the math; you provide the context.')}
            </Text>
          </Paper>

          <SectionTitle icon={<ShieldCheck size={16} />} title={t('3. Intellectual Property')} />
          <Title order={3} fz="lg">
            {t('A. Your Content')}
          </Title>
          <Text>
            {t('You retain all ownership rights to your source code and data. Timestamp GIT does not claim any license or ownership over the content you timestamp.')}
          </Text>
          <Title order={3} fz="lg">
            {t('B. Our Service')}
          </Title>
          <Text>
            {t('The Timestamp GIT dashboard, API, and Docker images are the intellectual property of RSJ Software GmbH. You are granted a non-exclusive, non-transferable license to use these services according to your subscription tier.')}
          </Text>

          <SectionTitle icon={<CreditCard size={16} />} title={t('4. Fees and Payment')} />
          <List>
            <List.Item>
              <Text component="span" fw={700}>
                {t('Billing:')}{' '}
              </Text>
              {t('Fees are billed in advance on a monthly or annual basis depending on your plan.')}
            </List.Item>
            <List.Item>
              <Text component="span" fw={700}>
                {t('Changes:')}{' '}
              </Text>
              {t('You can upgrade or downgrade your plan at any time. Downgrades take effect at the end of the current billing cycle.')}
            </List.Item>
            <List.Item>
              <Text component="span" fw={700}>
                {t('Refunds:')}{' '}
              </Text>
              {t('Since blockchain anchoring incurs immediate, irreversible computational and transaction costs, we generally do not offer refunds once hashes have been anchored.')}
            </List.Item>
          </List>

          <SectionTitle icon={<TriangleAlert size={16} />} title={t('5. Blockchain Specific Provisions')} />
          <Text>
            {t('By using this service, you acknowledge and agree to the unique properties of blockchain technology:')}
          </Text>
          <List>
            <List.Item>
              <Text component="span" fw={700}>
                {t('Immutability:')}{' '}
              </Text>
              {t('Once a Merkle Root is anchored to the Bitcoin blockchain, it is permanent. We cannot modify, hide, or delete these records.')}
            </List.Item>
            <List.Item>
              <Text component="span" fw={700}>
                {t('Decentralization:')}{' '}
              </Text>
              {t('Our service relies on the Bitcoin network. We are not responsible for delays caused by network congestion or protocol changes in the underlying blockchain.')}
            </List.Item>
            <List.Item>
              <Text component="span" fw={700}>
                {t('Transparency:')}{' '}
              </Text>
              {t('While commit hashes are opaque, the fact that a specific hash was anchored at a specific time is a public record.')}
            </List.Item>
          </List>

          <SectionTitle icon={<Ban size={16} />} title={t('6. Prohibited Conduct')} />
          <Text>{t('You agree not to use the Service to:')}</Text>
          <List>
            <List.Item>{t('Timestamp any content that is illegal, defamatory, or violates third-party IP rights.')}</List.Item>
            <List.Item>{t('Attempt to reverse-engineer or disrupt our batching and anchoring engine.')}</List.Item>
            <List.Item>{t('Use the API or GitHub Action in a way that causes excessive load or "spamming" of the Bitcoin mempool.')}</List.Item>
          </List>

          <SectionTitle icon={<Gavel size={16} />} title={t('7. Limitation of Liability')} />
          <Paper withBorder p="md" radius="md">
            <Text tt="uppercase" fz="sm" lh={1.8}>
              {t('TO THE MAXIMUM EXTENT PERMITTED BY LAW, RSJ SOFTWARE GMBH SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, OR CONSEQUENTIAL DAMAGES, INCLUDING LOSS OF PROFITS OR DATA. OUR TOTAL LIABILITY FOR ANY CLAIM SHALL NOT EXCEED THE TOTAL FEES PAID BY YOU IN THE 12 MONTHS PRIOR TO THE CLAIM.')}
            </Text>
          </Paper>

          <SectionTitle icon={<Server size={16} />} title={t('8. Enterprise Docker License')} />
          <Text>
            {t('For customers using our Self-Hosted Docker deployment: The license is limited to the number of instances specified in your Enterprise contract. You are responsible for the security and maintenance of the environment where the Docker container is deployed.')}
          </Text>

          <SectionTitle icon={<Mail size={16} />} title={t('9. Termination')} />
          <Text>
            {t('We may suspend or terminate your account if you violate these Terms. Upon termination, your access to the dashboard and API will cease. Existing ')}
            <CodeInline>.ots</CodeInline>
            {t(' proofs in your Shadow Repository will remain your property.')}
          </Text>

          <SectionTitle icon={<Mail size={16} />} title={t('10. Contact Information')} />
          <Text>{t('Questions about the Terms of Service should be sent to:')}</Text>

          <Paper radius="md" p="lg" withBorder>
            <Stack gap="xs">
              <Text>
                <Text component="span" fw={700}>
                  {t('Legal Department:')}{' '}
                </Text>
                <Anchor href="mailto:legal@timestampgit.com">legal@timestampgit.com</Anchor>
              </Text>
              <Text>RSJ Software GmbH</Text>
              <Text>Holzstrasse 4</Text>
              <Text>82110 Germering, Germany</Text>
            </Stack>
          </Paper>
        </Stack>
      </Container>
    </Box>
  );
}
